import React from "react";
import { css } from "@linaria/core";
import Text from "@ui/Text";
import ButtonIcon from "@ui/ButtonIconV2";
import { CloseLine } from "@ui/IconV2/react";
import { colors } from "@ui/Theme";
import Button from "@ui/Button";
import t from "../../../../../../../../../i18n";
import LoadingBoundary from "@ui/LoadingBoundary";
import type { Project } from "../../../../../../../../../api/useProjects";
import ErrorBoundary from "../../../../../../../../ErrorBoundary";
import { useProjectSource } from "../../../../../../../../../api/useSources";
import ProjectComment from "../../../../../../../../Comment";
import ProjectActivity from "../../../../../../../Activity";
import SidePanel from "../../../../components/SidePanel";
import type { ManualFaultEvent } from "../../../../../../../../../api/useManualFaultEvents";
import { zIndex } from "@ui";
import { EMBEDED_ACTIVITY } from "../../../../../../../../Activity/ActivityGroup";
import {
  FILTER_ACTIVITY_HEIGHT_CSS_VARIABLE,
  FAULT_EVENT_SIDE_PANEL_HEIGHT_CSS_VARIABLE,
} from "@ui/Header/useHeaderHeight";
import { useSidePanelOpened, useSidePanelTab } from "../../../../../../../useSidePanel";
import { matchRouterPath, toPath } from "../../../../../../../../../router/helpers/path";
import { useLocation, useNavigate } from "react-router";

interface Props {
  project: Project;
  isFolded: boolean;
  manualFaultEvent: ManualFaultEvent;
  mode: "comments" | "activity";
  closePanel: () => void;
}

const Activity = ({ isFolded, project, manualFaultEvent, mode, closePanel }: Props) => {
  const source = useProjectSource(project);
  const navigate = useNavigate();
  const location = useLocation();
  const [, setProjectSidePanelTab] = useSidePanelTab({ scope: project.id });
  const [, setProjectSidePanelOpened] = useSidePanelOpened({ scope: project.id });

  const openProjectSidePanel = React.useCallback(() => {
    setProjectSidePanelTab(mode);
    setProjectSidePanelOpened(true);

    let targetPath = toPath("/projects/:projectId/fault-events", {
      projectId: project.id,
    });
    const previousLocationWasClosedFaultEventsPage =
      location.state === null
        ? false
        : matchRouterPath("/projects/:projectId/fault-events/closed", location.state.from);

    if (previousLocationWasClosedFaultEventsPage) {
      targetPath = toPath("/projects/:projectId/fault-events/closed", {
        projectId: project.id,
      });
    }

    void navigate(targetPath);
  }, [
    location.state,
    mode,
    navigate,
    project.id,
    setProjectSidePanelOpened,
    setProjectSidePanelTab,
  ]);

  return (
    <SidePanel
      isFolded={isFolded}
      header={
        <>
          <div className={titleRow}>
            <Text type="h4">
              {mode === "comments"
                ? t("project.event.side_panel.comments.title")
                : t("project.event.side_panel.activity.title")}
            </Text>
            <Button variant="link" onClick={openProjectSidePanel} slim>
              {mode === "comments"
                ? t("project.event.side_panel.comments.project_link")
                : t("project.event.side_panel.activity.project_link")}
            </Button>
          </div>
          <ButtonIcon onClick={closePanel}>
            <CloseLine size="m" color={colors.carbon800} />
          </ButtonIcon>
        </>
      }
    >
      <ErrorBoundary>
        <LoadingBoundary>
          {mode === "comments" && source ? (
            <div className={commentContainer}>
              <ProjectComment project={project} source={source} eventId={manualFaultEvent.id} />
            </div>
          ) : (
            <div />
          )}
          {mode === "activity" ? (
            <div className={activityContainer}>
              <ProjectActivity project={project} eventId={manualFaultEvent.id} />
            </div>
          ) : (
            <div />
          )}
        </LoadingBoundary>
      </ErrorBoundary>
    </SidePanel>
  );
};

export default Activity;

//#region CSS
const titleRow = css`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const commentContainer = css`
  flex: 1;

  display: flex;
  flex-direction: column;

  & > :nth-child(1) {
    position: sticky;
    top: var(${FAULT_EVENT_SIDE_PANEL_HEIGHT_CSS_VARIABLE});
  }

  & > :nth-child(2) {
    /* height: auto; */
    margin-top: auto;
  }

  & > :nth-child(3) {
    position: sticky;
    bottom: 0;
    z-index: 3;
  }
`;
const activityContainer = css`
  & > :nth-child(1) {
    position: sticky;
    top: var(${FAULT_EVENT_SIDE_PANEL_HEIGHT_CSS_VARIABLE});
    background-color: ${colors.hydrogen};
    z-index: ${zIndex.sticky};
  }

  & > :nth-child(2) {
    height: auto;
    overflow: visible;

    & > .${EMBEDED_ACTIVITY} {
      position: sticky;
      top: calc(
        var(${FAULT_EVENT_SIDE_PANEL_HEIGHT_CSS_VARIABLE}) +
          var(${FILTER_ACTIVITY_HEIGHT_CSS_VARIABLE})
      ); /* hearder:105px + filtre:107px */
    }
  }
`;
//#endregion CSS
